import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Form } from "react-bootstrap";
import { TriyoForm } from "../../../../../../components/TriyoForm";
import SyncMemberSearch from "./SyncMemberSearch.jsx";
import "./SyncMemberPermissions.scss";

// The four base Member Permissions. Each maps 1:1 to the boolean flag stored on
// a TaskAutomationSyncPermission row (and on tblWorkItemUsers / tblWorkItemGroups).
const PERMISSIONS = [
  { key: "owner", label: "Owner", flag: "IsOwner" },
  { key: "editor", label: "Editor", flag: "CanEdit" },
  { key: "approver", label: "Approver", flag: "CanApprove" },
  { key: "viewer", label: "Viewer", flag: "CanView" },
];

const permissionKeyFromDto = (perm) => {
  if (perm.IsOwner) return "owner";
  if (perm.CanEdit) return "editor";
  if (perm.CanApprove) return "approver";
  if (perm.CanView) return "viewer";
  return null;
};

const emptyRow = () => ({
  selected: false,
  addTriggerUser: false,
  addSpecificUsersGroups: false,
  members: [],
});

// Resolve a stored { UserId | GroupId } target into a { Id, Name, IsGroup, Missing } chip
// using the department member list (falls back to "#id" when not resolvable). A target that
// is absent from a *loaded* roster has been removed from the Department and is flagged Missing
// so the UI can grey it out + tooltip it (TAD-5797).
const resolveMember = (m, departmentMembers) => {
  const isGroup = m.GroupId !== null && m.GroupId !== undefined;
  const id = isGroup ? m.GroupId : m.UserId;
  const list = departmentMembers || [];
  const match = list.find(
    (d) => String(d.Id) === String(id) && !!d.IsGroup === isGroup
  );
  return {
    Id: id,
    IsGroup: isGroup,
    // Prefer the live department name; fall back to the name resolved server-side (works for
    // members who have left the Department), then to "#id".
    Name: (match && match.Name) || m.Name || `#${id}`,
    // Empty list means "roster still loading", not "everyone removed" — only flag once loaded.
    Missing: list.length > 0 && !match,
    // Still in the Department but deactivated — greyed like removed members.
    Inactive: !!(match && match.Inactive),
    // Soft-deleted record (resolved server-side) — distinguishes "Archived" from "moved away".
    Archived: !!m.Archived,
  };
};

// Backend config DTO -> UI model (4 permission rows).
export const dtoToUi = (value, departmentMembers) => {
  const ui = {
    enabled: !!value?.Enabled,
    permissions: {
      owner: emptyRow(),
      editor: emptyRow(),
      approver: emptyRow(),
      viewer: emptyRow(),
    },
  };
  (value?.Permissions || []).forEach((perm) => {
    const key = permissionKeyFromDto(perm);
    if (!key) return;
    ui.permissions[key] = {
      selected: true,
      addTriggerUser: !!perm.AddTriggerUser,
      addSpecificUsersGroups: !!perm.AddSpecificUsersGroups,
      members: (perm.Members || []).map((m) => resolveMember(m, departmentMembers)),
    };
  });
  return ui;
};

// UI model -> backend config DTO (one Permissions entry per selected permission).
export const uiToDto = (ui) => ({
  Enabled: !!ui.enabled,
  Permissions: PERMISSIONS.filter((p) => ui.permissions[p.key].selected).map((p) => {
    const row = ui.permissions[p.key];
    return {
      IsOwner: p.key === "owner",
      CanEdit: p.key === "editor",
      CanApprove: p.key === "approver",
      CanView: p.key === "viewer",
      AddTriggerUser: !!row.addTriggerUser,
      AddSpecificUsersGroups: !!row.addSpecificUsersGroups,
      // Name/Archived ride along so a member who has left the Department keeps their resolved
      // display name + state across UI round-trips; the backend ignores both on save.
      Members: row.members.map((m) =>
        m.IsGroup
          ? { UserId: null, GroupId: m.Id, Name: m.Name, Archived: m.Archived }
          : { UserId: m.Id, GroupId: null, Name: m.Name, Archived: m.Archived }
      ),
    };
  }),
});

// Validation (AC3). Returns an error string, or "" when valid.
export const validateSyncConfig = (value) => {
  if (!value?.Enabled) return "";
  const perms = value.Permissions || [];
  if (perms.length === 0) {
    return "Select at least one Member Permission, or toggle Sync Member Permissions off.";
  }
  for (const perm of perms) {
    const key = permissionKeyFromDto(perm);
    const label = (PERMISSIONS.find((p) => p.key === key) || {}).label || "this permission";
    const hasOperation =
      perm.AddTriggerUser || (perm.AddSpecificUsersGroups && (perm.Members || []).length > 0);
    if (!hasOperation) {
      return `Configure at least one Operation for ${label}.`;
    }
  }
  return "";
};

const SyncMemberPermissions = ({ value, onChange, departmentMembers, disabled }) => {
  const ui = useMemo(() => dtoToUi(value, departmentMembers), [value, departmentMembers]);
  const validationError = useMemo(() => validateSyncConfig(value), [value]);

  const pushUi = (nextUi) => onChange(uiToDto(nextUi));

  // The toggle only enables/disables the automation — the (unsaved) configuration is kept either
  // way, so an accidental Off/On round-trip is lossless. Saving while Off still discards the
  // config server-side (SaveSyncConfigForStatus deletes it when not Enabled). Nothing is
  // pre-selected on enable (TAD-5797 AC 2.a: all permissions unchecked by default).
  const handleToggle = (checked) => {
    const next = dtoToUi(value, departmentMembers);
    next.enabled = checked;
    pushUi(next);
  };

  const updateRow = (key, patch) => {
    const next = dtoToUi(value, departmentMembers);
    next.enabled = true;
    next.permissions[key] = { ...next.permissions[key], ...patch };
    pushUi(next);
  };

  const togglePermission = (key, selected) => {
    const next = dtoToUi(value, departmentMembers);
    next.enabled = true;
    if (selected) {
      // AC 2.b: the Operations start unchecked — nothing is pre-selected for any permission.
      next.permissions[key] = { ...next.permissions[key], selected: true };
    } else {
      next.permissions[key] = emptyRow();
    }
    pushUi(next);
  };

  const addMember = (key, member) => {
    const next = dtoToUi(value, departmentMembers);
    next.enabled = true;
    const row = next.permissions[key];
    const exists = row.members.some(
      (m) => String(m.Id) === String(member.Id) && !!m.IsGroup === !!member.IsGroup
    );
    if (!exists) {
      row.members = [...row.members, { Id: member.Id, Name: member.Name, IsGroup: !!member.IsGroup }];
    }
    pushUi(next);
  };

  const removeMember = (key, member) => {
    const next = dtoToUi(value, departmentMembers);
    next.enabled = true;
    next.permissions[key].members = next.permissions[key].members.filter(
      (m) => !(String(m.Id) === String(member.Id) && !!m.IsGroup === !!member.IsGroup)
    );
    pushUi(next);
  };

  return (
    <div className="workflow-modal-section sync-member-permissions">
      <div className="workflow-modal-description">
        <div className="workflow-modal-description-title">Automation</div>
        <div className="workflow-modal-description-subtitle">
          Configure automations that fire when a Task enters this Status.
        </div>
      </div>

      <div className="workflow-modal-input sync-member-permissions-body">
        <div className="sync-toggle-row">
          <div className="sync-toggle-text">
            <div className="sync-toggle-title">Sync Member Permissions</div>
            <div className="sync-toggle-subtitle">
              Automatically add Users/Groups to selected Member Permissions when a Task enters this Status.
            </div>
          </div>
          <TriyoForm.Toggle
            id="sync-member-permissions-toggle"
            checked={!!ui.enabled}
            disabled={disabled}
            onChange={(e) => handleToggle(e.target.checked)}
          />
        </div>

        {ui.enabled && (
          <div className="sync-config-block">
            <div className="sync-config-label">Select Member Permission(s) to sync.</div>

            {PERMISSIONS.map((p) => {
              const row = ui.permissions[p.key];
              return (
                <div key={p.key} className="sync-permission-row">
                  <Form.Check
                    type="checkbox"
                    id={`sync-perm-${p.key}`}
                    label={p.label}
                    checked={!!row.selected}
                    disabled={disabled}
                    onChange={(e) => togglePermission(p.key, e.target.checked)}
                    data-testid={`sync-perm-${p.key}`}
                  />
                  {row.selected && (
                    <div className="sync-permission-operations">
                      <Form.Check
                        type="checkbox"
                        id={`sync-trigger-${p.key}`}
                        label="Add the person who triggers this State Transition"
                        checked={!!row.addTriggerUser}
                        disabled={disabled}
                        onChange={(e) => updateRow(p.key, { addTriggerUser: e.target.checked })}
                        data-testid={`sync-trigger-${p.key}`}
                      />
                      <Form.Check
                        type="checkbox"
                        id={`sync-specific-${p.key}`}
                        label="Add specific Users/Groups"
                        checked={!!row.addSpecificUsersGroups}
                        disabled={disabled}
                        onChange={(e) => updateRow(p.key, { addSpecificUsersGroups: e.target.checked })}
                        data-testid={`sync-specific-${p.key}`}
                      />
                      {row.addSpecificUsersGroups && (
                        <SyncMemberSearch
                          permissionKey={p.key}
                          members={row.members}
                          departmentMembers={departmentMembers}
                          onAdd={(m) => addMember(p.key, m)}
                          onRemove={(m) => removeMember(p.key, m)}
                          disabled={disabled}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {validationError && (
              <div className="sync-validation-error" data-testid="sync-validation-error">
                {validationError}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

SyncMemberPermissions.propTypes = {
  value: PropTypes.shape({
    Enabled: PropTypes.bool,
    Permissions: PropTypes.array,
  }),
  onChange: PropTypes.func.isRequired,
  departmentMembers: PropTypes.arrayOf(
    PropTypes.shape({
      Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      Name: PropTypes.string,
      IsGroup: PropTypes.bool,
    })
  ),
  disabled: PropTypes.bool,
};

SyncMemberPermissions.defaultProps = {
  value: null,
  departmentMembers: [],
  disabled: false,
};

export default SyncMemberPermissions;
